class NotificationService {
  constructor() {
    this.permission = 'default';
    this.audioContext = null;
    this.notificationSound = null;
    this.initializeAudioContext();
    this.requestPermission();
  }

  async requestPermission() {
    if ('Notification' in window) {
      this.permission = await Notification.requestPermission();
    }
  }

  initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.createNotificationSound();
    } catch (error) {
      console.warn('Audio context not supported:', error);
    }
  }

  createNotificationSound() {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, this.audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime + 0.2);
    
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
    
    this.notificationSound = { oscillator, gainNode };
  }

  async playNotificationSound() {
    try {
      if (!this.audioContext) {
        this.initializeAudioContext();
      }

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      this.createNotificationSound();
      const { oscillator } = this.notificationSound;
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
      this.fallbackSound();
    }
  }

  fallbackSound() {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwss5OzwygdePh3y9/nZCzM/dT/8zw==');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (error) {
      console.warn('Fallback sound also failed:', error);
    }
  }

  showNotification(title, options = {}) {
    if (this.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'quote-request',
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      setTimeout(() => notification.close(), 5000);
      
      return notification;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return null;
    }
  }

  /**
   * Handle Odoo quote request created notification
   */
  async notifyQuoteRequestCreated(data) {
    // data comes from Odoo WebSocket in format: { type: 'quote_request_created', payload: {...} }
    const quoteRequest = data; // Odoo already provides the payload directly
    
    const title = 'New Quote Request Created';
    const body = quoteRequest.message || `Quote request ${quoteRequest.name} has been created`;
    
    const notification = this.showNotification(title, {
      body,
      icon: '/favicon.ico',
      tag: `quote-created-${quoteRequest.id}`,
      requireInteraction: quoteRequest.action_required || false,
      onClick: () => {
        // Navigate to quotes page or specific quote
        if (quoteRequest.portal_url) {
          window.open(quoteRequest.portal_url, '_blank');
        } else {
          // Fallback to your internal routing
          window.location.href = `/quotes/${quoteRequest.id}`;
        }
      }
    });

    await this.playNotificationSound();
    
    return notification;
  }

  /**
   * Handle Odoo quote request updated notification (assignments, state changes, etc.)
   */
  async notifyQuoteRequestUpdated(data) {
    const quoteRequest = data; // Odoo payload
    
    let title = 'Quote Request Updated';
    let urgency = 'normal';
    
    // Determine notification urgency and title based on Odoo data
    if (quoteRequest.action_required) {
      title = 'New Quote Assignment';
      urgency = 'high';
    } else if (quoteRequest.state_change) {
      const newState = quoteRequest.state_change.to;
      title = `Quote Request ${newState.charAt(0).toUpperCase() + newState.slice(1)}`;
    } else if (quoteRequest.assignment_change) {
      title = 'Quote Reassigned';
    }
    
    const body = quoteRequest.message || `Quote request ${quoteRequest.name} has been updated`;
    
    const notification = this.showNotification(title, {
      body,
      icon: '/favicon.ico',
      tag: `quote-updated-${quoteRequest.id}`,
      requireInteraction: quoteRequest.action_required || urgency === 'high',
      onClick: () => {
        if (quoteRequest.portal_url) {
          window.open(quoteRequest.portal_url, '_blank');
        } else {
          window.location.href = `/quotes/${quoteRequest.id}`;
        }
      }
    });

    // Play sound for urgent notifications
    if (urgency === 'high' || quoteRequest.action_required) {
      await this.playNotificationSound();
    }
    
    return notification;
  }

  /**
   * Handle Odoo quote request deleted notification
   */
  async notifyQuoteRequestDeleted(data) {
    const quoteRequest = data; // Odoo payload
    
    const title = 'Quote Request Deleted';
    const body = quoteRequest.message || `Quote request ${quoteRequest.name} has been deleted`;
    
    const notification = this.showNotification(title, {
      body,
      icon: '/favicon.ico',
      tag: `quote-deleted-${quoteRequest.id}`,
      onClick: () => {
        // Navigate back to quotes list
        window.location.href = '/quotes';
      }
    });

    // Don't play sound for deletions (less urgent)
    
    return notification;
  }

  /**
   * Updated method - now handles both old format and new Odoo format
   */
  async notifyNewQuoteRequest(quoteRequest) {
    // Check if this is Odoo format (has message field) or old format
    if (quoteRequest.message) {
      // This is from Odoo WebSocket - use new handler
      return this.notifyQuoteRequestCreated(quoteRequest);
    }
    
    // Keep existing logic for backward compatibility
    const title = 'New Quote Request';
    const body = `${quoteRequest.name || `Request #${quoteRequest.id}`} has been created`;
    
    const notification = this.showNotification(title, {
      body,
      icon: '/favicon.ico',
      tag: `quote-${quoteRequest.id}`,
      onClick: () => {
        if (window.location.pathname !== '/quotes') {
          window.location.href = '/quotes';
        }
      }
    });

    await this.playNotificationSound();
    
    return notification;
  }

  /**
   * Updated method - now handles both old format and new Odoo format
   */
  async notifyQuoteRequestUpdate(quoteRequest) {
    // Check if this is Odoo format
    if (quoteRequest.message || quoteRequest.state_change || quoteRequest.assignment_change) {
      // This is from Odoo WebSocket - use new handler
      return this.notifyQuoteRequestUpdated(quoteRequest);
    }
    
    // Keep existing logic for backward compatibility
    const title = 'Quote Request Updated';
    const body = `${quoteRequest.name || `Request #${quoteRequest.id}`} status: ${quoteRequest.state_display || quoteRequest.state}`;
    
    const notification = this.showNotification(title, {
      body,
      icon: '/favicon.ico',
      tag: `quote-${quoteRequest.id}`,
      onClick: () => {
        window.location.href = `/quotes/${quoteRequest.id}`;
      }
    });

    await this.playNotificationSound();
    
    return notification;
  }

  isSupported() {
    return 'Notification' in window;
  }

  hasPermission() {
    return this.permission === 'granted';
  }
}

const notificationService = new NotificationService();
export default notificationService;